import { LitElement, html, css } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import * as TagNames from './combo-box.tagnames.ts';
import { FilterOption, ComboBoxProps } from './combo-box.props.ts';
import { SelectionChangedEvent, SearchChangedEvent, OrderChangedEvent } from './combo-box.events.ts';
import { getGlobalStyleSheets } from '../../global-styles.ts';
import { KEY_CODES } from '../event-constants.ts';

@customElement(TagNames.ComboBox)
export class ComboBox extends LitElement implements ComboBoxProps {
    static styles = [
        ...getGlobalStyleSheets(),
        css`

        .chip-search-input {
            background: transparent;
            color: var(--tti-form-common-color);
        }

        .chip-search-input:focus {
            outline: none;
            box-shadow: none;
        }

        .combobox-btns > button {
            color: var(--tti-chip-combo-box-icon-color);
        }

        .combobox-btns > button:disabled,
        .chip-combobox-container.disabled .combobox-btns > button {
            color: inherit;
        }

        .delete-btn {
            color: var(--tti-chip-combo-box-delete-icon-color);
        }

        .delete-btn:hover {
            transform: scale(1.3);
        }

        .chip-combobox-dropdown-menu {
            width: 100%;
            margin: 0.25rem 0;
            padding: 0.5rem 0;
            justify-content: center;
        }

        .sortable-list {
            list-style: none;
            margin: 0;
            padding: 0;
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
        }

        .sortable-item {
            display: flex;
            align-items: center;
            gap: 0.5rem;
        }

        .sortable-item.drag-over {
            outline: 2px dashed var(--tti-chip-combo-box-icon-color);
            outline-offset: 2px;
        }

        .sortable-item.dragging {
            opacity: 0.5;
        }

        .drag-handle {
            cursor: grab;
            color: var(--tti-chip-combo-box-icon-color);
            touch-action: none;
        }

        .drag-handle:active {
            cursor: grabbing;
        }

        i.drag-handle {
            margin-left: auto;
            opacity: 0.6;
        }
        
        [part="sortable-hint"] {
            opacity: 0.6;
        }

        .sortable-index {
            min-width: 1.25rem;
            text-align: right;
            color: var(--tti-form-common-color);
            opacity: 0.6;
        }

        .sortable-item .chip {
            flex: 0 0 auto;
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;            
        }

        .chip:active {
            border-color: var(--tti-chip-border-color);
            color: var(--tti-chip-text-color);
            background-color: var(--tti-chip-bg-color);
            font-weight: 400;
        }
    `];

    @property({ type: String })
    placeholder = 'Search...';

    @property({ type: Boolean })
    disabled = false;

    @property({ type: Number })
    debounce = 0;

    @property({ type: Array })
    items: FilterOption[] = [];

    @property({ type: Array })
    selectedItems: FilterOption[] = [];

    @property({ type: Boolean, attribute: 'drop-up' })
    dropUp = false;

    @property({ type: Boolean, attribute: 'hide-chips' })
    hideChips = false;

    @property({ type: Boolean })
    sortable = false;

    @state()
    private _searchText = '';

    @state()
    private _dropdownOpen = false;

    @state()
    private _focusedIndex: number = -1;

    @state()
    private _dragOverId: string | null = null;

    @query('.dropdown-menu')
    private _dropdownMenu?: HTMLElement;

    @query('.chip-combobox-container')
    private _container?: HTMLElement;

    @query('.search-row')
    private _searchRow?: HTMLElement;

    private _clickHandler?: (e: Event) => void;
    private _releaseHandler?: () => void;
    private _resizeHandler?: () => void;
    private _scrollHandler?: (e: Event) => void;
    private _debounceTimer?: number;

    @state()
    private _draggedId: string | null = null;

    protected override updated(changedProperties: Map<PropertyKey, unknown>): void {
        super.updated(changedProperties);

        if (changedProperties.has('dropUp') && this._dropdownOpen) {
            this._positionDropdown();
        }
    }

    connectedCallback() {
        super.connectedCallback();
        this._clickHandler = (e: Event) => this._handleOutsideClick(e);
        document.addEventListener('click', this._clickHandler);
        this._releaseHandler = () => { this._draggedId = null; };
        document.addEventListener('mouseup', this._releaseHandler);        
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        if (this._clickHandler) {
            document.removeEventListener('click', this._clickHandler);
        }
        if (this._releaseHandler) {
            document.removeEventListener('mouseup', this._releaseHandler);            
        }
        this._removeDropdownPositioning();
        if (this._debounceTimer) {
            clearTimeout(this._debounceTimer);
        }
    }

    private _handleOutsideClick(e: Event) {
        if (this._dropdownOpen && !this.contains(e.target as Node)) {
            this._closeDropdown();
        }
    }

    private _handleSearchInput(e: Event) {
        const input = e.target as HTMLInputElement;
        this._searchText = input.value;

        if (!this._dropdownOpen) {
            this._openDropdown();
        }

        if (this._debounceTimer) {
            clearTimeout(this._debounceTimer);
        }

        if (this.debounce > 0) {
            this._debounceTimer = window.setTimeout(() => {
                this._dispatchSearchChange();
            }, this.debounce);
        } else {
            this._dispatchSearchChange();
        }
    }

    private _handleFocusIn() {
        this._openDropdown();
        this._focusedIndex = -1;
    }

    private _handleInputKeyDown(e: KeyboardEvent) {
        if (e.key === KEY_CODES.ENTER) {
            this._toggleFocusedItem();
            e.preventDefault();
            return;
        }

        if (!this._dropdownOpen || this.items.length === 0) return;

        const totalCount = this.items.length + 1;
        switch (e.key) {
            case KEY_CODES.ARROW_DOWN:
                this._focusedIndex = (this._focusedIndex + 1) % totalCount;
                this._updateFocus();
                e.preventDefault();
                break;
            case KEY_CODES.ARROW_UP:
                this._focusedIndex = totalCount - 1;
                this._updateFocus();
                e.preventDefault();
                break;
            case KEY_CODES.ESCAPE:
                this._closeDropdown();
                e.preventDefault();
                break;
        }
    }

    private _handleDropdownItemKeyDown(e: KeyboardEvent, idx: number, item?: FilterOption) {
        const totalCount = this.items.length + 1;

        switch (e.key) {
            case KEY_CODES.ARROW_DOWN:
                if (idx === totalCount - 1) {
                    this._focusSearchInput();
                } else {
                    this._focusedIndex = idx + 1;
                    this._updateFocus();
                }
                e.preventDefault();
                break;
            case KEY_CODES.ARROW_UP:
                if (idx === 0) {
                    this._focusSearchInput();
                } else {
                    this._focusedIndex = idx - 1;
                    this._updateFocus();
                }
                e.preventDefault();
                break;
            case KEY_CODES.ENTER:
            case KEY_CODES.SPACE:
                if (idx === 0) {
                    this._triggerSelectAllCheckbox();
                } else if (item) {
                    this._toggleItemSelection(item);
                }
                e.preventDefault();
                break;
            case KEY_CODES.ESCAPE:
                this._focusSearchInput();
                this._closeDropdown();
                e.preventDefault();
                break;
        }
    }

    private _focusSearchInput() {
        this._focusedIndex = -1;
        (this.shadowRoot?.querySelector('.chip-search-input') as HTMLElement)?.focus();
    }

    private _updateFocus() {
        this._scrollFocusedItemIntoView();
        this._focusDropdownLabel(this._focusedIndex);
    }

    private _toggleFocusedItem() {
        if (this._focusedIndex === -1 && !this._dropdownOpen) {
            this._openDropdown();
        }
    }

    private _toggleItemSelection(item: FilterOption) {
        const isSelected = this._isItemSelected(item);
        this.selectedItems = isSelected
            ? this.selectedItems.filter(x => x.id !== item.id)
            : [...this.selectedItems, item];
        this._dispatchSelectionChange();
    }

    private _triggerSelectAllCheckbox() {
        const checkbox = this.shadowRoot?.querySelector('.dropdown-menu label input[type="checkbox"]') as HTMLInputElement;
        if (checkbox) {
            checkbox.checked = !checkbox.checked;
            checkbox.dispatchEvent(new Event('change', { bubbles: true }));
        }
    }

    private _focusDropdownLabel(idx: number) {
        if (!this._dropdownMenu) return;
        const items = this._dropdownMenu.querySelectorAll('[data-dropdown-index]');
        if (idx >= 0 && idx < items.length) {
            (items[idx] as HTMLElement).focus();
        }
    }

    private _scrollFocusedItemIntoView() {
        if (!this._dropdownMenu) return;
        const items = this._dropdownMenu.querySelectorAll('[data-dropdown-index]');
        if (this._focusedIndex >= 0 && this._focusedIndex < items.length) {
            const el = items[this._focusedIndex] as HTMLElement;
            el?.scrollIntoView({ block: 'nearest' });
        }
    }

    private _openDropdown() {
        this._dropdownOpen = true;
        this.requestUpdate();
        this.updateComplete.then(() => {
            this._positionDropdown();
            if (this._dropdownMenu) {
                this._dropdownMenu.scrollTop = 0;
            }
            this._addDropdownPositioning();
        });
    }

    private _closeDropdown() {
        this._dropdownOpen = false;
        this._removeDropdownPositioning();
    }

    private _toggleDropdown() {
        if (this._dropdownOpen) {
            this._closeDropdown();
        } else {
            this._openDropdown();
        }
    }

    private _clearSearch() {
        if (this._debounceTimer) {
            clearTimeout(this._debounceTimer);
        }
        this._searchText = '';
        this._dispatchSearchChange();
    }

    private _removeItem(item: FilterOption) {
        this.selectedItems = this.selectedItems.filter(x => x.id !== item.id);
        this._dispatchSelectionChange();
    }

    private _toggleSelectAll(e: Event) {
        const checkbox = e.target as HTMLInputElement;
        if (checkbox.checked) {
            const newSelections = [...this.selectedItems];
            this.items.forEach(item => {
                if (!newSelections.some(x => x.id === item.id)) {
                    newSelections.push(item);
                }
            });
            this.selectedItems = newSelections;
        } else {
            this.selectedItems = this.selectedItems.filter(
                x => !this.items.some(f => f.id === x.id)
            );
        }
        this._dispatchSelectionChange();
    }

    private _toggleItem(e: Event, item: FilterOption) {
        const checkbox = e.target as HTMLInputElement;
        if (checkbox.checked) {
            this.selectedItems = [...this.selectedItems, item];
        } else {
            this.selectedItems = this.selectedItems.filter(x => x.id !== item.id);
        }
        this._dispatchSelectionChange();
    }

    private _dispatchSelectionChange() {
        this.dispatchEvent(new SelectionChangedEvent(this.selectedItems));
    }

    private _dispatchSearchChange() {
        this.dispatchEvent(new SearchChangedEvent(this._searchText));
    }

    private _dispatchOrderChange() {
        this.dispatchEvent(new OrderChangedEvent(this.selectedItems));
    }

    private _isAllFilteredSelected(): boolean {
        return this.items.length > 0 &&
            this.items.every(i => this.selectedItems.some(x => x.id === i.id));
    }

    private _isItemSelected(item: FilterOption): boolean {
        return this.selectedItems.some(x => x.id === item.id);
    }

    private _positionDropdown() {
        if (!this._dropdownMenu || !this._container || !this._searchRow) return;

        const row = this._searchRow;
        const dropdown = this._dropdownMenu;

        if (this.dropUp) {
            dropdown.style.top = 'auto';
            dropdown.style.bottom = '';
            return;
        }

        const rowRect = row.getBoundingClientRect();
        const dropdownHeight = dropdown.offsetHeight;
        const spaceBelow = window.innerHeight - rowRect.bottom;
        const spaceAbove = rowRect.top;
        const openUp = spaceBelow < dropdownHeight && spaceAbove > dropdownHeight;

        dropdown.classList.toggle('dropdown-menu-up', openUp);

        if (openUp) {
            dropdown.style.top = 'auto';
            dropdown.style.bottom = `calc(100% - ${row.offsetTop}px)`;
        } else {
            dropdown.style.top = `${row.offsetTop + row.offsetHeight}px`;
            dropdown.style.bottom = 'auto';
        }
    }

    private _addDropdownPositioning() {
        this._resizeHandler = () => setTimeout(() => this._positionDropdown(), 0);
        this._scrollHandler = () => setTimeout(() => this._positionDropdown(), 0);
        window.addEventListener('resize', this._resizeHandler);
        window.addEventListener('scroll', this._scrollHandler, true);
    }

    private _removeDropdownPositioning() {
        if (this._resizeHandler) {
            window.removeEventListener('resize', this._resizeHandler);
            this._resizeHandler = undefined;
        }
        if (this._scrollHandler) {
            window.removeEventListener('scroll', this._scrollHandler, true);
            this._scrollHandler = undefined;
        }
    }

    private _handleDragStart(e: DragEvent, item: FilterOption) {
        this._draggedId = item.id;
        if (e.dataTransfer) {
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/plain', item.id);
        }
    }

    private _handleDragOver(e: DragEvent, item: FilterOption) {
        e.preventDefault();
        if (e.dataTransfer) {
            e.dataTransfer.dropEffect = 'move';
        }
        if (this._draggedId && this._draggedId !== item.id) {
            this._dragOverId = item.id;
        }
    }

    private _handleDragLeave(item: FilterOption) {
        if (this._dragOverId === item.id) {
            this._dragOverId = null;
        }
    }

    private _handleDrop(e: DragEvent, targetItem: FilterOption) {
        e.preventDefault();
        this._dragOverId = null;
        const draggedId = this._draggedId;
        this._draggedId = null;
        if (!draggedId || draggedId === targetItem.id) return;

        const items = [...this.selectedItems];
        const fromIndex = items.findIndex(x => x.id === draggedId);
        const toIndex = items.findIndex(x => x.id === targetItem.id);
        if (fromIndex === -1 || toIndex === -1) return;

        const [moved] = items.splice(fromIndex, 1);
        items.splice(toIndex, 0, moved);
        this.selectedItems = items;
        this._dispatchOrderChange();
    }

    private _handleDragEnd() {
        this._draggedId = null;
        this._dragOverId = null;
    }    

    render() {
        return html`
            <div class="dropdown chip-combobox-container ${this.disabled ? 'disabled' : ''}">
                <div class="form-control pe-1" part="combo-box-search">
                    <div class="search-row d-flex justify-content-between align-items-center ${this.disabled ? 'disabled' : ''}">
                        <div class="d-flex col gap-2 align-items-center">
                            <i class="fa-solid fa-magnifying-glass background-icon" aria-hidden="true"></i>
                            <input
                                part="combo-box-input"
                                role="combobox"
                                aria-expanded="${this._dropdownOpen}"
                                aria-activedescendant="${this._focusedIndex >= 0 && this._focusedIndex <= this.items.length ? 'combo-box-item-' + this._focusedIndex : ''}"
                                class="chip-search-input border-0 w-100"
                                placeholder="${this.placeholder}"
                                .value="${this._searchText}"
                                ?disabled="${this.disabled}"
                                @input="${this._handleSearchInput}"
                                @focusin="${this._handleFocusIn}"
                                @keydown="${this._handleInputKeyDown}"
                            />
                        </div>
                        <div class="d-flex gap-2 align-items-center combobox-btns">
                            ${this._searchText ? html`
                                <button
                                    type="button"
                                    @click="${this._clearSearch}"
                                    title="Clear Search"
                                    class="btn btn-link w-100 btn-sm"
                                    aria-label="Clear all"
                                    ?disabled="${this.disabled}"
                                >
                                    <i aria-hidden="true" class="fa-solid fa-times"></i>
                                </button>
                                <div class="vr"></div>
                            ` : ''}
                            <button
                                type="button"
                                aria-label="toggle drop down"
                                class="btn btn-link w-100 btn-sm"
                                ?disabled="${this.disabled}"
                                @click="${this._toggleDropdown}"
                            >
                                <i aria-hidden="true" class="fa-solid ${this.dropUp ? 'fa-chevron-up' : 'fa-chevron-down'}"></i>
                            </button>
                        </div>
                    </div>
                    ${!this.hideChips && !this.sortable && this.selectedItems.length > 0 ? html`
                        <hr class="my-2"/>
                        <div part="chip-input-container" class="chip-input-container d-flex align-items-center flex-wrap gap-1 combobox-btns">
                            ${repeat(this.selectedItems, (item) => item.id, (item) => html`
                                <span class="chip">
                                    ${item.displayName}
                                    ${this.disabled ? '' : html`
                                        <button
                                            type="button"
                                            ?disabled="${this.disabled}"
                                            @click="${() => this._removeItem(item)}"
                                            aria-label="delete"
                                            class="delete-btn btn p-0 btn-sm"
                                        >
                                            <i class="delete-icon fa-solid fa-xmark"></i>
                                        </button>
                                    `}
                                </span>
                            `)}
                        </div>
                    ` : ''}
                </div>
                <div part="combo-box-dropdown" role="listbox" aria-multiselectable="true" class="dropdown-menu chip-combobox-dropdown-menu ${this._dropdownOpen ? 'show' : ''} ${this.dropUp ? 'dropdown-menu-up' : ''}">
                    ${this.items.length > 0 ? html`
                        <label
                            role="option"
                            tabindex="0"
                            class="dropdown-item form-check-label fw-bold py-0 clickable"
                            data-dropdown-index="0"
                            aria-selected="${this._isAllFilteredSelected()}"
                            @keydown="${(e: KeyboardEvent) => this._handleDropdownItemKeyDown(e, 0)}"
                            @focus="${() => { this._focusedIndex = 0; }}"
                        >
                            <input
                                type="checkbox"
                                tabindex="-1"
                                class="form-check-input me-2"
                                .checked="${this._isAllFilteredSelected()}"
                                @change="${this._toggleSelectAll}"
                            />
                            Select All
                        </label>
                        ${repeat(this.items, item => item.id, (item, idx) => {
                            const dropdownIdx = idx + 1;
                            return html`
                            <label
                                role="option"
                                part="combo-box-dropdown-item"
                                tabindex="0"
                                class="dropdown-item form-check-label py-0 fw-normal clickable"
                                data-dropdown-index="${dropdownIdx}"
                                aria-selected="${this._isItemSelected(item)}"
                                @keydown="${(e: KeyboardEvent) => this._handleDropdownItemKeyDown(e, dropdownIdx, item)}"
                                @focus="${() => { this._focusedIndex = dropdownIdx; }}"
                            >
                                <input
                                    class="form-check-input me-2"
                                    type="checkbox"
                                    tabindex="-1"
                                    .checked="${this._isItemSelected(item)}"
                                    @change="${(e: Event) => this.sortable ? this._toggleItemSelection(item) : this._toggleItem(e, item)}"
                                />
                                ${item.displayName}
                            </label>
                        `;})}
                    ` : html`
                        <div class="dropdown-item d-flex justify-content-center">
                            <span>No results found</span>
                        </div>
                    `}
                </div>
                ${!this.hideChips && this.sortable && this.selectedItems.length > 0 ? html`
                    <hr class="my-2"/>
                    <p part="sortable-hint" class="text-muted mb-1"><i class="fa-solid fa-grip-vertical" aria-hidden="true"></i> Drag items to reorder</p>
                    <ol part="sortable-list" class="sortable-list">
                        ${repeat(this.selectedItems, (item) => item.id, (item, idx) => html`
                            <li
                                class="sortable-item ${this._dragOverId === item.id ? 'drag-over' : ''} ${this._draggedId === item.id ? 'dragging' : ''}"
                                draggable="${this.disabled ? 'false' : 'true'}"
                                @mousedown="${() => { this._draggedId = item.id; }}"
                                @mouseup="${() => { if (this._draggedId === item.id) { this._draggedId = null; } }}"
                                @dragstart="${(e: DragEvent) => this._handleDragStart(e, item)}"
                                @dragover="${(e: DragEvent) => this._handleDragOver(e, item)}"
                                @dragleave="${() => this._handleDragLeave(item)}"
                                @drop="${(e: DragEvent) => this._handleDrop(e, item)}"
                                @dragend="${this._handleDragEnd}"
                            >
                                <span class="sortable-index text-muted">${idx + 1}</span>
                                <span class="chip drag-handle">
                                    ${item.displayName}
                                    ${this.disabled ? '' : html`
                                        <button
                                            type="button"
                                            ?disabled="${this.disabled}"
                                            @click="${() => this._removeItem(item)}"
                                            aria-label="delete ${item.displayName}"
                                            class="delete-btn btn p-0 btn-sm"
                                        >
                                            <i class="delete-icon fa-solid fa-xmark"></i>
                                        </button>
                                    `}
                                </span>
                                ${this.disabled ? '' : html`
                                    <i
                                        class="drag-handle fa-solid fa-grip-vertical"
                                        aria-label="reorder ${item.displayName}"
                                    ></i>
                                `}
                            </li>
                        `)}
                    </ol>
                ` : ''}
            </div>
        `;
    }
}